import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, FileDown, ShieldCheck, Clock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import VialImage from "@/components/VialImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid}>
      <p className="font-tech text-2xl sm:text-3xl font-semibold text-emerald-400">{value}</p>
      <p className="text-xs text-slate-400 mt-1 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function CoaRow({ r, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(i % 8, 6) * 0.04 }}
      data-testid={`coa-row-${r.product_id}`}
      className="grid grid-cols-[56px_1fr] sm:grid-cols-[64px_1.6fr_1fr_1fr_auto] gap-4 items-center bg-white border border-slate-200 hover:border-sky-400 transition-colors p-4">
      <VialImage product={{ id: r.product_id, name: r.name, alias: r.alias, specification: r.specification, coa_purity: r.purity, coa_lot: r.lot }} className="h-20 sm:h-24" />
      <div>
        <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400">{r.sku}</p>
        <h3 className="font-display font-semibold text-slate-900 leading-snug">{r.name}</h3>
        <p className="text-xs text-slate-500">{r.alias ? `${r.alias} · ` : ""}{r.specification}</p>
      </div>
      <div className="col-start-2 sm:col-start-auto">
        <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400">Batch / Lot</p>
        <p className="font-tech text-sm text-slate-900 mt-0.5">{r.lot || (r.reference_coa ? `Ref. ${r.reference_coa.lot}` : "Assigned at dispatch")}</p>
      </div>
      <div className="col-start-2 sm:col-start-auto">
        <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400">HPLC Purity</p>
        {r.purity ? (
          <>
            <p className="font-tech text-sm font-semibold text-emerald-700 mt-0.5">{r.purity}%</p>
            <div className="h-1 bg-slate-100 mt-1.5 w-full max-w-[140px]">
              <div className="h-1 bg-emerald-500" style={{ width: `${Math.max(0, (r.purity - 95) / 5) * 100}%` }} />
            </div>
          </>
        ) : (
          <p className="font-tech text-sm text-slate-500 mt-0.5">{r.has_file ? "See PDF (sterility/ID panel)" : r.reference_coa ? `Ref. lot ${r.reference_coa.purity ? `${r.reference_coa.purity}%` : "tested"}` : "Per-batch at dispatch"}</p>
        )}
      </div>
      <div className="col-start-2 sm:col-start-auto">
        {r.has_file ? (
          <a href={`${API}/coa/${r.product_id}`} target="_blank" rel="noopener noreferrer" data-testid={`coa-library-download-${r.product_id}`}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white text-xs font-medium px-4 py-2.5 rounded-full transition-colors whitespace-nowrap">
            <FileDown className="h-3.5 w-3.5" /> Download PDF
          </a>
        ) : r.reference_coa ? (
          <a href={`${API}/coa/${r.reference_coa.product_id}`} target="_blank" rel="noopener noreferrer" data-testid={`coa-library-reference-${r.product_id}`}
            title="Reference COA — batch-matched COA ships with order"
            className="inline-flex flex-col items-start gap-0.5 border border-slate-300 hover:border-slate-900 text-slate-800 text-xs font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap">
            <span className="inline-flex items-center gap-2"><FileDown className="h-3.5 w-3.5" /> Reference COA ({r.reference_coa.specification.split(" ")[0]})</span>
            <span className="font-tech text-[9px] uppercase tracking-wide text-slate-500">Batch-matched COA ships with order</span>
          </a>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 whitespace-nowrap" data-testid={`coa-library-pending-${r.product_id}`}>
            <Clock className="h-3.5 w-3.5" /> Ships with order
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function CoaLibrary() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(() => {
    axios.get(`${API}/coa-library`).then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const verified = useMemo(() => rows.filter((r) => r.has_file), [rows]);
  const avg = useMemo(() => {
    const p = verified.filter((r) => r.purity);
    return p.length ? (p.reduce((a, r) => a + r.purity, 0) / p.length).toFixed(2) : "—";
  }, [verified]);

  const filtered = rows.filter((r) =>
    (!verifiedOnly || r.has_file || r.reference_coa) &&
    `${r.name} ${r.alias || ""} ${r.sku} ${r.lot || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <main data-testid="coa-library-page">
      <section className="bg-[#0B111A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors" data-testid="coa-library-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to catalog
          </Link>
          <div className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
            <div>
              <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-400 mb-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> Independent Analytical Records
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">COA Library</h1>
              <p className="mt-4 text-base text-slate-300 max-w-xl leading-relaxed">
                Every lot Arias Peptides distributes is manufactured by Axis Bio Lab and assayed by an independent
                laboratory. Search any compound, SKU or lot number and download the original Certificate of Analysis.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-6" data-testid="coa-library-stats">
              <Stat value={rows.length ? verified.length : "…"} label="Lots with PDF on file" testid="coa-stat-verified" />
              <Stat value={rows.length ? (avg === "—" ? avg : `${avg}%`) : "…"} label="Mean HPLC purity" testid="coa-stat-avg" />
              <Stat value={rows.length || "…"} label="Catalog SKUs" testid="coa-stat-total" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search compound, SKU or lot number..."
              data-testid="coa-library-search-input" className="pl-9 rounded-sm bg-white" />
          </div>
          <button onClick={() => setVerifiedOnly(!verifiedOnly)} data-testid="coa-library-verified-toggle"
            className={`font-tech text-xs uppercase tracking-wide px-3 py-2 border rounded-full transition-colors ${
              verifiedOnly ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
            PDF or reference COA only
          </button>
          <p className="font-tech text-xs text-slate-500 sm:ml-auto" data-testid="coa-library-count">{filtered.length} records</p>
        </div>
        <div className="space-y-2">
          {filtered.map((r, i) => <CoaRow key={r.product_id} r={r} i={i} />)}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-slate-500 py-16 font-tech text-sm" data-testid="coa-library-empty">No records match your search.</p>
        )}
        <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-3xl">
          Certificates are issued by the supplier's independent analytical laboratory and are reproduced unaltered.
          Arias Peptides is a research-supply marketplace; the brand shown on each vial is the manufacturer of record.
          All materials are for in-vitro laboratory research use only.
        </p>
      </section>
    </main>
  );
}
